/**
 * Aviator — the game page. PHASE D (2026-09-06): the table clock is now the
 * SERVER one (`./serverTableClock`), imported under the old name so this file
 * changed by one line of wiring plus the balance honesty gate below. Real
 * rounds, real rupees; `?practice=1` still hands back the Phase-A local clock.
 *
 * THE BALANCE ON SCREEN IS THE SERVER'S OR IT IS NOTHING. Until the clock's
 * first `/state` lands, `walletReady()` is false: the figure reads `₹—`, the
 * chips are disabled and every bet entry point refuses. Nothing here decides
 * affordability against a number this app made up.
 *
 * The page derives every phase from the clock's round timestamps + status
 * and nothing else. React renders structure + low-frequency state (≤10 Hz
 * from the clock); ONE rAF (only while flying) writes the canvas curve, the
 * plane's position vars, the readout and the sky — never React state.
 *
 * All shown multipliers are TOTAL RETURN at 2 dp (X-doctrine).
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { mAt, tFor, payout, fmtX, bandOf, q2, WINDOW_MS, type PanelState } from './engine';
import { ServerTableClock as LocalTableClock, isPracticeTable, type TableClock, type Round, type Panel, type Wallet } from './serverTableClock';
import { crowdFor, standingsFor, seatsFor, type FakeBettor } from './fakes';
import { mix } from './prng';
import {
  PLANE_SVG, RUNWAY_SVG, CITY_SVG, STARS_SVG, AURORA_SVG, SMOKE_SVG,
  CLOUDS_NEAR_SVG, CLOUDS_MID_SVG, CLOUDS_FAR_SVG,
} from './art';
import {
  initAvSound, isAvSoundOn, setAvSoundOn, unlockAvSound, initAvMusic, isAvMusicOn, setAvMusicOn,
  startAvMusic, stopAvMusic, bindAvVisibility, avBedPhase,
  sTakeoff, sFlightTick, sCrash, sHeartbeatStop, sCashout, sBet, sCancel, sDenied, sTooLate, sLock, sTap, sCrowdTick,
} from './sound';
import './aviator.css';

/* press(): the platform tap contract — pointerdown arms, pointerup fires */
const armed = new WeakMap<Element, number>();
function press(fn: () => void) {
  return {
    onPointerDown: (e: React.PointerEvent) => { e.stopPropagation(); armed.set(e.currentTarget, e.pointerId); },
    onPointerUp: (e: React.PointerEvent) => {
      e.stopPropagation();
      if (armed.get(e.currentTarget) !== e.pointerId) return;
      armed.delete(e.currentTarget);
      if ((e.currentTarget as HTMLButtonElement).disabled) return;
      fn();
    },
    onPointerCancel: (e: React.PointerEvent) => { armed.delete(e.currentTarget); },
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      if (e.repeat) return;
      e.preventDefault(); e.stopPropagation();
      if ((e.currentTarget as HTMLButtonElement).disabled) return;
      fn();
    },
  };
}

// Opening balance BEFORE the server has spoken. It used to be 10,000 — the Phase-A practice
// bankroll — which meant a real-money table showed the player ₹10,000 they did not have until
// the first `/state` landed, and every affordability check (`bet()`, the "poor" chips, the ADD
// MONEY face) was decided against that fiction. Zero plus the `walletReady` gate below means
// nothing is ever shown or spent against a number we invented.
// (2026-09-06; the practice clock funds itself, so `?practice=1` is unaffected.)
const PRACTICE_START = 0;
const CHIPS = [10, 50, 100, 500, 1000, 5000];
const AUTO_PRESETS = [1.5, 2, 3, 5];
const TABLE_NAME = 'SKY LOUNGE';
const inr = (n: number) => `₹${n.toLocaleString('en-IN')}`;

/* the camera (sky spec §4): fixed until the plane reaches the pin at X2,
   then the world zooms out beneath it */
const CAM = { x0: 0.36, y0: 0.86, xP: 0.80, yP: 0.30, gamma: 0.7, T0: Math.log(2) / 0.11, M0: 2 };
const uOf = (m: number) => Math.pow(Math.max(0, m - 1), CAM.gamma);
function project(t: number, tp: number, mp: number, W: number, S: number): [number, number] {
  const Tx = Math.max(CAM.T0, t), Mtop = Math.max(CAM.M0, Math.exp(0.11 * t));
  return [(CAM.x0 + (tp / Tx) * (CAM.xP - CAM.x0)) * W, (CAM.y0 - (uOf(mp) / uOf(Mtop)) * (CAM.y0 - CAM.yP)) * S];
}
const BAND_HEX = { low: '#3EA6FF', mid: '#B04CFF', high: '#FF3FA4' } as const;
const smooth = (x: number, a: number, b: number) => { const t = Math.min(1, Math.max(0, (Math.log(x) - Math.log(a)) / (Math.log(b) - Math.log(a)))); return t * t * (3 - 2 * t); };

const lsGet = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
const lsSet = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* private mode */ } };

type Strip = { text: string; cls: string };
type TickRow = { id: string; name: string; x: number; amt: number; you?: boolean };
type Stamp = { id: number; kind: 'took' | 'flew'; text: string; x?: number; y?: number };
type Burst = { id: number; cx: number; cy: number };

const AvPage = () => {
  const appRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const planeRef = useRef<HTMLSpanElement>(null);
  const multRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const skyRefs = useRef<(HTMLDivElement | null)[]>([]);
  const starsRef = useRef<HTMLDivElement>(null);
  const auroraRef = useRef<HTMLDivElement>(null);
  const cloudRefs = useRef<(HTMLDivElement | null)[]>([]);
  const cityRef = useRef<HTMLDivElement>(null);
  const runwayRef = useRef<HTMLDivElement>(null);
  const walletRef = useRef<HTMLSpanElement>(null);

  /* the balance sink — the server clock DRIVES this to the figure `/state` reports */
  const balRef = useRef(PRACTICE_START);
  const [balance, setBalance] = useState(PRACTICE_START);
  const [walletFx, setWalletFx] = useState('');
  const wallet = useMemo<Wallet>(() => ({
    balance: () => balRef.current,
    debit: (n) => { if (balRef.current < n) return false; balRef.current -= n; setBalance(balRef.current); setWalletFx('dip'); window.setTimeout(() => setWalletFx(''), 400); return true; },
    credit: (n) => { balRef.current += n; setBalance(balRef.current); },
  }), []);

  const clockRef = useRef<TableClock | null>(null);
  const [tick, setTick] = useState(0);
  // Re-read every tick: the clock flips this the moment its first /state (or a write ack) lands.
  const walletReady = clockRef.current?.walletReady() ?? false;
  const [status, setStatus] = useState<Round['status']>('betting');
  const [roundNo, setRoundNo] = useState(0);
  const [chip, setChip] = useState<[number, number]>(() => {
    const a = Number(lsGet('av_chip_A')) || 100, b = Number(lsGet('av_chip_B')) || 100;
    return [CHIPS.includes(a) ? a : 100, CHIPS.includes(b) ? b : 100];
  });
  const [auto, setAuto] = useState<[{ on: boolean; x: number }, { on: boolean; x: number }]>(() => {
    const rd = (k: string) => { try { const v = JSON.parse(lsGet(k) || ''); if (v && typeof v.x === 'number') return { on: !!v.on, x: q2(v.x) }; } catch { /* default */ } return { on: false, x: 2 }; };
    return [rd('av_auto_A'), rd('av_auto_B')];
  });
  const [presetsFor, setPresetsFor] = useState<Panel | null>(null);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [hint, setHint] = useState(() => lsGet('av_hint_v1') !== '1');
  const [soundOn, setSoundOn] = useState(initAvSound);
  const [musicOn, setMusicOn] = useState(initAvMusic);
  const [event, setEvent] = useState<(Strip & { until: number }) | null>(null);
  const [rows, setRows] = useState<TickRow[]>([]);
  const [stamps, setStamps] = useState<Stamp[]>([]);
  const [bursts, setBursts] = useState<Burst[]>([]);
  const [smoke, setSmoke] = useState<{ x: number; y: number; d: number }[]>([]);
  const [swapKey, setSwapKey] = useState<[number, number]>([0, 0]);
  const [countdown, setCountdown] = useState(6);
  const [takes, setTakes] = useState<{ name: string; amt: number; you?: boolean }[]>([]);

  const crowdRef = useRef<FakeBettor[]>([]);
  const revealedRef = useRef(0);
  const lastRevealRef = useRef(0);
  const lastTipRef = useRef<[number, number]>([0, 0]);
  const rafRef = useRef(0);
  const prevStatusRef = useRef<Round['status'] | 'boot'>('boot');
  const bandRef = useRef<'low' | 'mid' | 'high'>('low');
  const lastMultTextRef = useRef('');
  const lastSoundTickRef = useRef(0);
  const youSawRef = useRef(1);
  const timersRef = useRef<number[]>([]);
  const later = useCallback((fn: () => void, ms: number) => { timersRef.current.push(window.setTimeout(fn, ms)); }, []);

  /* --av-w/--av-h must be TRUE (content box, integer px) */
  useLayoutEffect(() => {
    const el = appRef.current; if (!el) return;
    const write = (w: number, h: number) => {
      el.style.setProperty('--av-w', `${Math.round(w)}px`);
      el.style.setProperty('--av-h', `${Math.round(h)}px`);
      sizeCanvas();
    };
    const cs = getComputedStyle(el);
    write(el.clientWidth, el.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom));
    const ro = new ResizeObserver((es) => { const r = es[es.length - 1].contentRect; write(r.width, r.height); });
    ro.observe(el, { box: 'content-box' });
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sizeCanvas = () => {
    const cv = canvasRef.current, st = stageRef.current; if (!cv || !st) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = st.clientWidth, h = st.clientHeight;
    if (cv.width !== Math.round(w * dpr)) { cv.width = Math.round(w * dpr); cv.height = Math.round(h * dpr); }
  };

  /* the clock — the real table (`serverTableClock`); `?practice=1` hands back the local one */
  useEffect(() => {
    let seed = Number(lsGet('av_table_seed'));
    if (!seed) { seed = (Math.random() * 0xffffffff) >>> 0; lsSet('av_table_seed', String(seed)); }
    const clock = new LocalTableClock(seed, wallet, 40 + (seed % 60));
    clockRef.current = clock;
    const unsub = clock.subscribe(() => { setTick((t) => t + 1); });
    // `round()` is null until the first `/state` lands — a real table has no round to show
    // before the server names one, and inventing one would put a fabricated takeoff on screen.
    setRoundNo(clock.round()?.no ?? 0);
    if (isAvMusicOn() && isAvSoundOn()) startAvMusic();
    const unbind = bindAvVisibility();
    return () => { unsub(); clock.destroy(); unbind(); stopAvMusic(); cancelAnimationFrame(rafRef.current); timersRef.current.forEach(window.clearTimeout); };
  }, [wallet]);

  const clock = clockRef.current;
  const round = clock?.round();
  const st = round?.status ?? 'betting';

  /* ── phase transitions (derived from the clock, never from timers) ── */
  useEffect(() => {
    if (!clock || !round) return;
    if (st === prevStatusRef.current && round.no === roundNo) return;
    const prev = prevStatusRef.current;
    const booting = prev === 'boot';
    prevStatusRef.current = st;
    setStatus(st);
    if (round.no !== roundNo) setRoundNo(round.no);

    if (st === 'betting') {
      crowdRef.current = crowdFor(round.fakeSeed, round.no);
      revealedRef.current = 0;
      setTakes([]);
      setStamps([]); setSmoke([]);
      bandRef.current = 'low'; lastMultTextRef.current = '';
      const mult = multRef.current; if (mult) { mult.className = 'av-mult av-mult--hidden av-num'; mult.querySelector('b')!.textContent = '1.00'; }
      const pl = planeRef.current; if (pl) { pl.className = 'av-plane is-idle'; }
      resetSky();
      placePlaneAtOrigin();
      avBedPhase('betting');
      setEvent(null);
    }
    if (st === 'locked') {
      sLock();
      const pl = planeRef.current; if (pl) pl.className = 'av-plane is-rev';
      runwayRef.current?.classList.add('sweep');
      later(() => runwayRef.current?.classList.remove('sweep'), 1100);
    }
    if (st === 'flying') {
      sTakeoff();
      navigator.vibrate?.(12);
      const pl = planeRef.current; if (pl) pl.className = 'av-plane is-flying';
      const mult = multRef.current; if (mult) mult.className = 'av-mult av-mult--low av-num';
      startRaf();
    }
    if (booting && st !== 'betting') { crowdRef.current = crowdFor(round.fakeSeed, round.no); placePlaneAtOrigin(); }
    if (st === 'crashed' && prev !== 'crashed') {
      cancelAnimationFrame(rafRef.current);
      sCrash();
      const M = round.M ?? 1;
      drawFrame(tFor(M), M, true);
      const mult = multRef.current;
      if (mult) { mult.querySelector('b')!.textContent = q2(M).toFixed(2); mult.className = 'av-mult av-mult--flew av-num'; later(() => { if (multRef.current) multRef.current.className = 'av-mult av-mult--grey av-num'; }, 700); }
      const pl = planeRef.current; if (pl) pl.className = 'av-plane is-away';
      const [tx, ty] = lastTipRef.current;
      setSmoke([0, 1, 2, 3, 4].map((i) => ({ x: tx - 30 + i * 22, y: ty - i * 26, d: i * 90 })));
      setStamps((s) => [...s, { id: Date.now(), kind: 'flew', text: `FLEW AWAY AT ${fmtX(M)}` }]);
      /* the panels' fates */
      ([0, 1] as Panel[]).forEach((p) => {
        const ps = clock.panel(p);
        if (ps.kind === 'lost') { showEvent(`${prefix(p)}FLEW AWAY AT ${fmtX(M)} — LOST ${inr(ps.stake)}`, 'red'); bump(p); }
      });
      if (([0, 1] as Panel[]).some((p) => clock.panel(p).kind === 'lost')) navigator.vibrate?.([120, 60, 120]);
    }
    if (st === 'intermission') { avBedPhase('intermission'); later(() => { if (planeRef.current) planeRef.current.className = 'av-plane is-hidden'; }, 100); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [st, round?.no, tick]);

  /* auto-cashouts are executed by the clock; surface them */
  const seenCashRef = useRef<[number, number]>([0, 0]);
  useEffect(() => {
    if (!clock) return;
    ([0, 1] as Panel[]).forEach((p) => {
      const ps = clock.panel(p);
      if (ps.kind === 'cashed' && seenCashRef.current[p] !== ps.won + ps.m * 1000) {
        seenCashRef.current[p] = ps.won + ps.m * 1000;
        celebrate(p, ps.m, ps.won, true);
      }
      if (ps.kind !== 'cashed' && seenCashRef.current[p] !== 0 && ps.kind === 'idle') seenCashRef.current[p] = 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick]);

  /* betting countdown text (hard-swapped each second) */
  useEffect(() => {
    if (st !== 'betting' || !round) return;
    const id = window.setInterval(() => {
      const left = Math.max(0, Math.ceil((round.locksAt - performance.now()) / 1000));
      setCountdown(left);
    }, 200);
    return () => window.clearInterval(id);
  }, [st, round]);

  const prefix = (p: Panel) => {
    const c = clockRef.current; if (!c) return '';
    const other = c.panel(p === 0 ? 1 : 0);
    return other.kind === 'idle' ? '' : (p === 0 ? 'A: ' : 'B: ');
  };
  const showEvent = useCallback((text: string, cls: string, hold = 3000) => {
    setEvent({ text, cls, until: performance.now() + hold });
    later(() => setEvent((e) => (e && performance.now() >= e.until - 5 ? null : e)), hold + 10);
  }, [later]);
  const bump = (p: Panel) => setSwapKey((k) => (p === 0 ? [k[0] + 1, k[1]] : [k[0], k[1] + 1]));

  /* ── the sky + curve, per frame ──────────────────────────────────── */
  const resetSky = () => {
    skyRefs.current.forEach((el, i) => { if (el) el.style.opacity = i === 0 ? '1' : '0'; });
    if (starsRef.current) starsRef.current.style.opacity = '0';
    if (auroraRef.current) auroraRef.current.style.opacity = '0';
    cloudRefs.current.forEach((el) => { if (el) { el.style.opacity = '1'; el.style.transform = 'translateY(0)'; } });
    if (cityRef.current) { cityRef.current.style.opacity = '1'; cityRef.current.style.transform = 'translateY(0)'; }
    if (runwayRef.current) runwayRef.current.style.opacity = '1';
    const cv = canvasRef.current; const ctx = cv?.getContext('2d'); if (cv && ctx) ctx.clearRect(0, 0, cv.width, cv.height);
  };
  const placePlaneAtOrigin = () => {
    const stg = stageRef.current; if (!stg) return;
    const W = stg.clientWidth, S = stg.clientHeight;
    const [x, y] = project(0, 0, 1, W, S);
    stg.style.setProperty('--av-px', `${x.toFixed(1)}px`);
    stg.style.setProperty('--av-py', `${y.toFixed(1)}px`);
    stg.style.setProperty('--av-ang', '0deg');
    lastTipRef.current = [x, y];
  };
  const skyAt = (m: number) => {
    const anchors = [1, 3, 10, 50, 200].map(Math.log);
    const L = Math.log(Math.min(500, Math.max(1, m)));
    for (let i = 0; i < 5; i++) {
      const l0 = anchors[i], l1 = anchors[i + 1] ?? Infinity, lp = anchors[i - 1] ?? -Infinity;
      let w = 0;
      if (L >= l0 && L < l1) w = l1 === Infinity ? 1 : 1 - (L - l0) / (l1 - l0);
      else if (L >= lp && L < l0) w = (L - lp) / (l0 - lp);
      const el = skyRefs.current[i]; if (el) el.style.opacity = w.toFixed(3);
    }
    if (starsRef.current) starsRef.current.style.opacity = smooth(m, 8, 15).toFixed(3);
    if (auroraRef.current) auroraRef.current.style.opacity = smooth(m, 40, 80).toFixed(3);
    const S = stageRef.current?.clientHeight ?? 300;
    const lnm = Math.min(Math.log(m), 6.2);
    const desc = [0.06, 0.12, 0.22];
    const fade = [[12, 22], [4, 7], [2, 3.5]];
    cloudRefs.current.forEach((el, i) => {
      if (!el) return;
      el.style.opacity = (1 - smooth(m, fade[i][0], fade[i][1])).toFixed(3);
      el.style.transform = `translateY(${(desc[i] * S * lnm).toFixed(1)}px)`;
    });
    if (cityRef.current) { cityRef.current.style.opacity = (1 - smooth(m, 6, 12)).toFixed(3); cityRef.current.style.transform = `translateY(${(0.05 * S * lnm).toFixed(1)}px)`; }
    if (runwayRef.current) runwayRef.current.style.opacity = (1 - 0.65 * smooth(m, 6, 15)).toFixed(3);
  };
  const drawFrame = (t: number, m: number, dimmed: boolean) => {
    const cv = canvasRef.current, stg = stageRef.current; if (!cv || !stg) return;
    const ctx = cv.getContext('2d'); if (!ctx) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const W = stg.clientWidth, S = stg.clientHeight;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, S);
    const band = bandOf(m);
    const col = BAND_HEX[band];
    const N = 96;
    const pts: number[] = [];
    for (let i = 0; i <= N; i++) { const ti = t * i / N; const [x, y] = project(t, ti, Math.exp(0.11 * ti), W, S); pts.push(x, y); }
    const [x0, y0] = project(t, 0, 1, W, S);
    const tipX = pts[N * 2], tipY = pts[N * 2 + 1];
    const g = ctx.createLinearGradient(0, tipY, 0, y0);
    g.addColorStop(0, col + '57'); g.addColorStop(.55, col + '1F'); g.addColorStop(1, col + '00');
    ctx.globalAlpha = dimmed ? .35 : 1;
    ctx.beginPath(); ctx.moveTo(x0, y0);
    for (let i = 0; i <= N; i++) ctx.lineTo(pts[i * 2], pts[i * 2 + 1]);
    ctx.lineTo(tipX, y0); ctx.closePath(); ctx.fillStyle = g; ctx.fill();
    ctx.beginPath(); ctx.moveTo(pts[0], pts[1]);
    for (let i = 1; i <= N; i++) ctx.lineTo(pts[i * 2], pts[i * 2 + 1]);
    ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.strokeStyle = col;
    const base = dimmed ? .35 : 1;
    ctx.globalAlpha = base * .10; ctx.lineWidth = .034 * W; ctx.stroke();
    ctx.globalAlpha = base * .20; ctx.lineWidth = .017 * W; ctx.stroke();
    ctx.globalAlpha = base; ctx.lineWidth = .007 * W; ctx.stroke();
    ctx.strokeStyle = '#FFFFFF'; ctx.globalAlpha = base * .5; ctx.lineWidth = .0028 * W;
    ctx.save(); ctx.translate(0, -.0035 * W); ctx.stroke(); ctx.restore();
    ctx.globalAlpha = 1;
    /* the plane's nose sits on the tip; heading from a 250ms back-sample */
    const [bx, by] = project(t, Math.max(0, t - .25), Math.exp(0.11 * Math.max(0, t - .25)), W, S);
    let ang = Math.atan2(tipY - by, tipX - bx) * 180 / Math.PI;
    ang = Math.max(-38, Math.min(4, ang));
    stg.style.setProperty('--av-px', `${tipX.toFixed(1)}px`);
    stg.style.setProperty('--av-py', `${tipY.toFixed(1)}px`);
    stg.style.setProperty('--av-ang', `${ang.toFixed(1)}deg`);
    lastTipRef.current = [tipX, tipY];
  };
  const startRaf = () => {
    cancelAnimationFrame(rafRef.current);
    const loop = () => {
      const c = clockRef.current; if (!c) return;
      const r = c.round();
      if (r?.status !== 'flying') return;
      const t = (c.serverNow() - r.takeoffAt) / 1000;
      const m = mAt(t);
      drawFrame(t, m, false);
      skyAt(m);
      /* readout ≤30 Hz, band pops */
      const txt = q2(m).toFixed(2);
      const mult = multRef.current;
      if (mult && txt !== lastMultTextRef.current) {
        lastMultTextRef.current = txt;
        mult.querySelector('b')!.textContent = txt;
        const band = bandOf(m);
        if (band !== bandRef.current) {
          bandRef.current = band;
          mult.className = `av-mult av-mult--${band} av-num pop`;
          later(() => mult.classList.remove('pop'), 300);
          if (band === 'high' && ringRef.current) { ringRef.current.classList.remove('go'); void ringRef.current.offsetWidth; ringRef.current.classList.add('go'); }
        }
      }
      youSawRef.current = m;
      if (t >= CAM.T0 && planeRef.current && !planeRef.current.classList.contains('is-cruise')) planeRef.current.className = 'av-plane is-cruise is-flying';
      const now = performance.now();
      if (now - lastSoundTickRef.current > 33) { lastSoundTickRef.current = now; sFlightTick(m); }
      /* the crowd cashes out as the multiplier passes them */
      revealCrowd(m, now);
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
  };
  const revealCrowd = (m: number, now: number) => {
    if (now - lastRevealRef.current < 280) return;
    const crowd = crowdRef.current;
    for (let i = revealedRef.current; i < crowd.length; i++) {
      const f = crowd[i];
      if (f.cashX != null && f.cashX <= m) {
        revealedRef.current = i + 1;
        lastRevealRef.current = now;
        const amt = payout(f.stake, f.cashX);
        setRows((r) => [{ id: f.id, name: f.name, x: f.cashX!, amt }, ...r].slice(0, 12));
        setTakes((tk) => [...tk, { name: f.name, amt }].sort((a, b) => b.amt - a.amt).slice(0, 6));
        sCrowdTick();
        return;
      }
      if (f.cashX == null) revealedRef.current = Math.max(revealedRef.current, i + 1);
    }
  };

  /* ── money moments ──────────────────────────────────────────────── */
  const celebrate = (p: Panel, m: number, won: number, isAuto: boolean) => {
    sCashout(m);
    navigator.vibrate?.([16, 24, 16, 24, 48]);
    showEvent(`${prefix(p)}${isAuto ? 'AUTO ' : ''}TOOK ${inr(won)} AT ${fmtX(m)}`, 'green');
    setRows((r) => [{ id: `you-${Date.now()}`, name: 'You', x: m, amt: won, you: true }, ...r].slice(0, 12));
    setTakes((tk) => [...tk.filter((t) => !t.you), { name: 'YOU', amt: won, you: true }].sort((a, b) => b.amt - a.amt).slice(0, 6));
    const [tx, ty] = lastTipRef.current;
    const stg = stageRef.current, app = appRef.current, wl = walletRef.current;
    if (stg && app && wl) {
      const sr = stg.getBoundingClientRect(), ar = app.getBoundingClientRect(), wr = wl.getBoundingClientRect();
      const cx = sr.left - ar.left + tx - 0.30 * ar.width, cy = sr.top - ar.top + ty;
      app.style.setProperty('--av-wx', `${(wr.left - ar.left + wr.width / 2).toFixed(0)}px`);
      app.style.setProperty('--av-wy', `${(wr.top - ar.top + wr.height / 2).toFixed(0)}px`);
      const id = Date.now();
      setBursts((b) => [...b, { id, cx, cy }]);
      later(() => setBursts((b) => b.filter((x) => x.id !== id)), 1100);
      later(() => { setWalletFx('gulp'); later(() => setWalletFx(''), 250); }, 720);
      setStamps((s) => [...s, { id, kind: 'took', text: `TOOK ${inr(won)} AT ${fmtX(m)}`, x: Math.max(12, tx - 0.32 * sr.width), y: Math.max(8, ty - 0.07 * sr.height) }]);
      later(() => setStamps((s) => s.filter((x) => x.id !== id)), 2000);
    }
    bump(p);
    if (([0, 1] as Panel[]).every((q) => clockRef.current?.panel(q).kind !== 'flying')) sHeartbeatStop();
  };

  const doBet = (p: Panel) => {
    const c = clockRef.current; if (!c) return;
    unlockAvSound();
    // Until the server has told us the balance we cannot say whether a stake is affordable, so
    // we do not let one be composed — the alternative is a bet the server refuses for a reason
    // the player was never shown. (2026-09-06)
    if (!c.walletReady()) { showEvent('Loading your balance…', 'red'); sDenied(); return; }
    const stake = chip[p];
    const res = c.bet(p, stake, auto[p].on ? auto[p].x : null);   // POST /aviator/bet
    if (res.ok === false) {
      if (res.reason === 'balance') showEvent(`Not enough balance for ${inr(stake)} — pick a smaller chip`, 'red');
      else if (res.reason === 'wallet' || res.reason === 'sync') showEvent('Loading your balance…', 'red');
      else if (res.reason === 'locked') showEvent('This table has no open round right now', 'red');
      sDenied(); return;
    }
    sBet(res.queued);
    navigator.vibrate?.(8);
    if (res.queued) showEvent(`${inr(stake)} queued — plays next round`, 'blue');
    if (hint) { setHint(false); lsSet('av_hint_v1', '1'); }
    bump(p);
  };
  const doCancel = (p: Panel) => {
    const c = clockRef.current; if (!c) return;
    const ps = c.panel(p);
    if (c.cancel(p)) { sCancel(); showEvent(`Bet cancelled — ${inr(ps.kind === 'placed' || ps.kind === 'queued' ? ps.stake : 0)} back in wallet`, ''); bump(p); }
    // The server closes `/unbet` half a second before the lock (paces.js lock_ms), so the last
    // instant of the window has a live CANCEL the table will no longer honour. Say so rather
    // than leaving a button that does nothing. (2026-09-06)
    else { showEvent('Too late to cancel — this round is closing', 'red'); sDenied(); }
  };
  // AWAITED on purpose (2026-09-06). A real cash out is priced by the server from the DB clock
  // at the instant the request lands, so the multiplier and the rupees celebrated here are the
  // ones the wallet actually moved by — never an optimistic figure the balance then contradicts.
  // The practice clock answers synchronously; `await` on a plain value costs it one microtask.
  const doCashout = async (p: Panel) => {
    const c = clockRef.current; if (!c) return;
    const saw = youSawRef.current;
    const res = await c.cashout(p);                                 // POST /aviator/cashout
    if (res.ok === false) {
      if (res.reason === 'too_late') {
        const M = c.round()?.M ?? saw;
        showEvent(`TOO LATE — flew at ${fmtX(M)} before your tap reached us (you saw ${fmtX(saw)})`, 'amber', 4000);
        sTooLate(); navigator.vibrate?.([40, 40, 40]);
      } else if (res.message) {
        showEvent(res.message, 'red', 4000);
        sDenied();
      }
      return;
    }
    seenCashRef.current[p] = res.won + res.m * 1000;   // the auto-detector must not fire again
    celebrate(p, res.m, res.won, false);
  };

  const setAutoFor = (p: Panel, v: { on: boolean; x: number }) => {
    setAuto((a) => { const n: [typeof v, typeof v] = [a[0], a[1]]; n[p] = v; lsSet(p === 0 ? 'av_auto_A' : 'av_auto_B', JSON.stringify(v)); return n; });
    showEvent(v.on ? `Auto cash out set to ${fmtX(v.x)}` : 'Auto cash out off', v.on ? 'green' : '');
    sTap();
  };
  const holdRef = useRef(0);
  const stepAuto = (p: Panel, dir: 1 | -1) => setAuto((a) => {
    const cur = a[p]; const nx = q2(Math.min(100, Math.max(1.1, cur.x + dir * 0.1)));
    if (nx === cur.x) return a;
    const n: typeof a = [a[0], a[1]]; n[p] = { on: true, x: nx }; lsSet(p === 0 ? 'av_auto_A' : 'av_auto_B', JSON.stringify(n[p])); return n;
  });
  const hold = (p: Panel, dir: 1 | -1) => ({
    onPointerDown: (e: React.PointerEvent) => { e.stopPropagation(); stepAuto(p, dir); sTap(); window.clearInterval(holdRef.current); let n = 0; holdRef.current = window.setInterval(() => { n++; if (n > 3) stepAuto(p, dir); }, 90); },
    onPointerUp: () => window.clearInterval(holdRef.current),
    onPointerCancel: () => window.clearInterval(holdRef.current),
    onPointerLeave: () => window.clearInterval(holdRef.current),
  });
  useEffect(() => () => window.clearInterval(holdRef.current), []);

  /* ── the strip: events beat the phase sentence ──────────────────── */
  const strip: Strip = (() => {
    if (event && performance.now() < event.until) return event;
    const c = clockRef.current;
    const anyLive = c ? ([0, 1] as Panel[]).some((p) => c.panel(p).kind === 'flying') : false;
    const anyPlaced = c ? ([0, 1] as Panel[]).some((p) => c.panel(p).kind === 'placed') : false;
    const anyQueued = c ? ([0, 1] as Panel[]).some((p) => c.panel(p).kind === 'queued') : false;
    const allAuto = c ? ([0, 1] as Panel[]).filter((p) => c.panel(p).kind === 'flying').every((p) => (c.panel(p) as { autoX: number | null }).autoX != null) : false;
    switch (st) {
      case 'betting': return anyPlaced ? { text: `You're in · taking off in ${countdown}s`, cls: 'gold' } : { text: `Bets open · ${countdown}s`, cls: 'gold' };
      case 'locked': return { text: 'Bets locked — taking off', cls: '' };
      case 'flying':
        if (anyLive) return allAuto ? { text: `Auto cash out armed`, cls: 'green' } : { text: 'Tap CASH OUT any time', cls: 'green' };
        if (anyQueued) return { text: 'Queued for next round', cls: 'blue' };
        return { text: 'Flying · bet now for the next round', cls: '' };
      case 'crashed': case 'intermission': return { text: `Flew away at ${fmtX(round?.M ?? 1)}`, cls: '' };
    }
  })();

  /* ── render helpers ─────────────────────────────────────────────── */
  const bigFace = (p: Panel) => {
    const c = clockRef.current;
    const ps: PanelState = c ? c.panel(p) : { kind: 'idle' };
    const stake = chip[p];
    // Affordability is only a question once the SERVER has told us the balance. Before that
    // `balance` is 0 because we refuse to invent one, and "ADD MONEY" would be a lie.
    const poor = walletReady && stake > balance;
    switch (ps.kind) {
      case 'idle':
        if (!walletReady) return { cls: 'av-big--locked', l1: 'LOADING YOUR BALANCE…', l2: '', on: () => {}, dis: true };
        if (poor) return { cls: 'av-big--poor', l1: 'ADD MONEY', l2: `NEED ${inr(stake)} · HAVE ${inr(balance)}`, on: () => showEvent(`Not enough balance for ${inr(stake)} — pick a smaller chip`, 'red'), dis: false };
        return { cls: 'av-big--bet', l1: st === 'betting' ? `BET ${inr(stake)}` : `◷ NEXT ROUND ${inr(stake)}`, l2: st === 'betting' ? '' : 'TAP TO QUEUE', on: () => doBet(p), dis: false };
      case 'placed':
        if (st === 'locked') return { cls: 'av-big--locked', l1: 'WAITING FOR TAKEOFF', l2: `${inr(ps.stake)} IN`, on: () => {}, dis: true };
        return { cls: 'av-big--wait', l1: 'WAITING FOR TAKEOFF', l2: 'CANCEL', on: () => doCancel(p), dis: false };
      case 'queued': return { cls: 'av-big--queued', l1: `◷ NEXT ROUND ${inr(ps.stake)}`, l2: 'CANCEL', on: () => doCancel(p), dis: false };
      case 'flying': {
        const m = c ? c.mNow() : 1;
        return { cls: 'av-big--live', l1: `CASH OUT ${inr(payout(ps.stake, m))}`, l2: ps.autoX != null ? `AUTO AT ${fmtX(ps.autoX)}` : fmtX(m), on: () => doCashout(p), dis: false };
      }
      case 'cashed': return { cls: 'av-big--stamp av-big--won', l1: `TOOK ${inr(ps.won)}`, l2: `AT ${fmtX(ps.m)}`, on: () => {}, dis: true };
      case 'lost': return { cls: 'av-big--stamp av-big--lost', l1: `FLEW AWAY AT ${fmtX(ps.M)}`, l2: `LOST ${inr(ps.stake)}`, on: () => {}, dis: true };
    }
  };

  const seats = round ? seatsFor(mix(round.fakeSeed, 7), round.no) : 200;
  const pot = crowdRef.current.reduce((s, f) => s + f.stake, 0) + (clock ? ([0, 1] as Panel[]).reduce((s, p) => { const ps = clock.panel(p); return s + ((ps.kind === 'placed' || ps.kind === 'flying' || ps.kind === 'cashed' || ps.kind === 'lost') ? ps.stake : 0); }, 0) : 0);
  const standings = round ? standingsFor(round.fakeSeed, round.no, 5000) : [];
  const board = (takes.length ? takes : standings.map((s) => ({ name: s.name, amt: s.net, you: false }))).slice(0, 3);
  const youTake = takes.find((t) => t.you);
  const hist = clock?.history() ?? [];
  const inPlay = st === 'locked' || st === 'flying';
  const frameCls = st === 'flying' || st === 'locked' ? 'on' : st === 'crashed' ? 'dead' : '';

  return (
    <div className="av-root fixed inset-0 z-[9999]">
    <div ref={appRef} className="av-app" onPointerDown={unlockAvSound}>
      <div className={`av-frame ${frameCls}`} aria-hidden />

      {/* A. ribbon */}
      <div className="av-ribbon">
        <div className="av-title"><b>AVIATOR</b><i>{TABLE_NAME} · #{roundNo}</i></div>
        {/* This table moves real rupees now. Only `?practice=1` still deals practice chips, and
            a real-money screen must never claim otherwise. (2026-09-06) */}
        <span className="av-ping"><i />{isPracticeTable() ? 'PRACTICE' : 'LIVE'}</span>
        <button type="button" className={`av-iconbtn ${soundOn ? '' : 'off'}`} aria-label="Sounds" {...press(() => setSoundOn(setAvSoundOn(!isAvSoundOn())))}>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden><path d="M4 9v6h4l5 5V4L8 9H4z" /><path d="M16.5 12a4.5 4.5 0 0 0-2.5-4v8a4.5 4.5 0 0 0 2.5-4z" /></svg>
        </button>
        <button type="button" className={`av-iconbtn ${musicOn ? '' : 'off'}`} aria-label="Music" {...press(() => setMusicOn(setAvMusicOn(!isAvMusicOn())))}>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden><path d="M9 17.2V6.3l10-2v10.9h-2V6.7l-6 1.2v9.3z" /><ellipse cx="7" cy="17.6" rx="2.9" ry="2.4" /><ellipse cx="17" cy="15.6" rx="2.9" ry="2.4" /></svg>
        </button>
        <span ref={walletRef} className={`av-wallet av-num ${walletFx}`}
              title={walletReady ? 'Your balance' : 'Loading your balance'}>{walletReady ? inr(balance) : '₹—'}</span>
      </div>

      {/* B. history */}
      <div className="av-history">
        <div className="av-hist-row">
          {hist.map((M, i) => (
            <span key={`${roundNo}-${i}`} className={`av-pill av-num av-pill--${bandOf(M)} ${i === 0 && (st === 'crashed' || st === 'intermission') ? 'av-pill--new' : ''}`}>{fmtX(M)}</span>
          ))}
        </div>
      </div>

      {/* C. the flight stage */}
      <div ref={stageRef} className="av-stage">
        {(['a', 'b', 'c', 'd', 'e'] as const).map((k, i) => <div key={k} ref={(el) => { skyRefs.current[i] = el; }} className={`av-sky av-sky-${k}`} />)}
        <div ref={starsRef} className="av-stars" dangerouslySetInnerHTML={{ __html: STARS_SVG }} />
        <div ref={auroraRef} className="av-aurora" dangerouslySetInnerHTML={{ __html: AURORA_SVG }} />
        <div ref={(el) => { cloudRefs.current[0] = el; }} className="av-clouds av-clouds--far" dangerouslySetInnerHTML={{ __html: CLOUDS_FAR_SVG }} />
        <div ref={(el) => { cloudRefs.current[1] = el; }} className="av-clouds av-clouds--mid" dangerouslySetInnerHTML={{ __html: CLOUDS_MID_SVG }} />
        <div ref={(el) => { cloudRefs.current[2] = el; }} className="av-clouds av-clouds--near" dangerouslySetInnerHTML={{ __html: CLOUDS_NEAR_SVG }} />
        <div ref={cityRef} className="av-city" dangerouslySetInnerHTML={{ __html: CITY_SVG }} />
        <div ref={runwayRef} className="av-runway" dangerouslySetInnerHTML={{ __html: RUNWAY_SVG }} />
        <canvas ref={canvasRef} className="av-curve" />
        <span ref={planeRef} className="av-plane is-idle" dangerouslySetInnerHTML={{ __html: PLANE_SVG }} />
        {clock && ([0, 1] as Panel[]).map((p) => clock.panel(p).kind === 'flying' && (
          <span key={p} className="av-tipchip av-chip" data-v={String((clock.panel(p) as { stake: number }).stake)} style={{ ['--av-i' as string]: p }} />
        ))}
        <div ref={multRef} className="av-mult av-mult--hidden av-num"><small>X</small><b>1.00</b></div>
        <div ref={ringRef} className="av-ring" aria-hidden />
        {st === 'betting' && (
          <div className="av-countdown" key={roundNo}><b>TAKEOFF IN {countdown}</b><i /><small>{seats.toLocaleString('en-IN')} betting · pot {inr(pot)}</small></div>
        )}
        <div className="av-lb">
          <b>TOP TAKES</b>
          {board.map((r, i) => <div key={i} className={r.you ? 'you' : ''}><span>{i + 1}. {r.name}</span><em>{inr(r.amt)}</em></div>)}
          {!youTake && <div className="you"><span>YOU</span><em>—</em></div>}
        </div>
        {stamps.map((s) => (
          <div key={s.id} className={`av-stamp av-stamp--${s.kind} av-num`} style={s.kind === 'took' ? { left: s.x, top: s.y } : undefined}>{s.text}</div>
        ))}
        {smoke.map((s, i) => (
          <span key={i} className="av-smoke" style={{ ['--av-sx' as string]: `${s.x}px`, ['--av-sy' as string]: `${s.y}px`, ['--av-d' as string]: `${s.d}ms` }} dangerouslySetInnerHTML={{ __html: SMOKE_SVG }} />
        ))}
      </div>

      {/* D. the strip */}
      <div className={`av-strip ${strip.cls}`} {...press(() => setRulesOpen(true))}>{strip.text}</div>

      {/* E. the dock */}
      <div className="av-dock">
        {([0, 1] as Panel[]).map((p) => {
          const face = bigFace(p);
          const a = auto[p];
          return (
            <div key={p} className="av-panel">
              <i>{p === 0 ? 'A' : 'B'}</i>
              <div className="av-chips">
                {CHIPS.map((c) => (
                  <button key={c} type="button" data-v={c} disabled={!walletReady}
                          className={`av-chip ${chip[p] === c ? 'sel' : ''} ${walletReady && c > balance ? 'poor' : ''}`}
                          aria-label={`₹${c} chip`} {...press(() => { setChip((ch) => { const n: [number, number] = [ch[0], ch[1]]; n[p] = c; lsSet(p === 0 ? 'av_chip_A' : 'av_chip_B', String(c)); return n; }); sTap(); })}>
                    {c >= 1000 ? `${c / 1000}k` : c}
                  </button>
                ))}
              </div>
              <div className="av-auto" style={{ position: 'relative' }}>
                {!a.on ? (
                  <button type="button" className="av-auto-pill" {...press(() => setAutoFor(p, { on: true, x: a.x }))}><b>AUTO CASH OUT</b><i /></button>
                ) : (
                  <>
                    <button type="button" className="av-auto-step" disabled={a.x <= 1.1} {...hold(p, -1)}>−</button>
                    <button type="button" className="av-auto-val av-num" {...press(() => setPresetsFor(presetsFor === p ? null : p))}><small>AUTO</small><b>{fmtX(a.x)}</b></button>
                    <button type="button" className="av-auto-step" disabled={a.x >= 100} {...hold(p, 1)}>+</button>
                  </>
                )}
                {presetsFor === p && (
                  <div className="av-presets">
                    {AUTO_PRESETS.map((x) => <button key={x} type="button" className={`av-num ${a.x === x ? 'cur' : ''}`} {...press(() => { setAutoFor(p, { on: true, x }); setPresetsFor(null); })}>{fmtX(x)}</button>)}
                    <button type="button" className="off" {...press(() => { setAutoFor(p, { on: false, x: a.x }); setPresetsFor(null); })}>TURN OFF</button>
                  </div>
                )}
              </div>
              <button key={`big-${swapKey[p]}`} type="button" className={`av-big av-num ${face.cls} swap`} disabled={face.dis} {...press(face.on)}>
                <span className={face.l1.length > 14 ? 'long' : ''}>{face.l1}</span>
                {face.l2 && <small>{face.l2}</small>}
              </button>
            </div>
          );
        })}
      </div>

      {/* F. ticker */}
      <div className={`av-ticker ${inPlay ? '' : ''}`}>
        <div className="av-tick-left av-num"><b>{seats.toLocaleString('en-IN')} betting</b><em>Pot {inr(pot)}</em></div>
        <div className="av-crawl">
          <div className="av-crawl-track" key={rows.length ? rows[0].id : 'empty'}>
            {[...rows, ...rows].map((r, i) => (
              <span key={`${r.id}-${i}`} className={`av-crawl-item av-num ${r.you ? 'you' : ''}`}>{r.name} took <b>{inr(r.amt)}</b> at <i>{fmtX(r.x)}</i></span>
            ))}
            {rows.length === 0 && <span className="av-crawl-item">Round #{roundNo} · {TABLE_NAME}</span>}
          </div>
        </div>
      </div>

      {/* overlays */}
      {bursts.length > 0 && (
        <div className="av-coins" aria-hidden>
          {bursts.map((b) => [-.10, -.06, -.02, .02, .06, .10, .14].map((kx, i) => (
            <span key={`${b.id}-${i}`} className="av-coin" style={{ ['--av-cx' as string]: `${b.cx}px`, ['--av-cy' as string]: `${b.cy}px`, ['--av-kx' as string]: `${kx * 100}px`, ['--av-d' as string]: `${i * 28}ms` }} />
          )))}
        </div>
      )}
      {hint && st === 'betting' && (
        <div className="av-hint">Tap BET, then tap CASH OUT before the plane flies away.<b {...press(() => { setHint(false); lsSet('av_hint_v1', '1'); })}>GOT IT</b></div>
      )}
      {rulesOpen && (
        <>
          <div className="av-backdrop" {...press(() => setRulesOpen(false))} />
          <div className="av-sheet">
            <i />
            <h3>HOW IT WORKS</h3>
            <ol>
              <li><b>Pick a chip, tap BET.</b> You have 6 seconds before takeoff. Two bets at once if you want.</li>
              <li><b>The plane climbs. The number climbs.</b> X2.35 means ₹100 becomes ₹235.</li>
              <li><b>Tap CASH OUT before it flies away.</b> Wait too long and you lose the stake.</li>
              <li><b>When your tap counts:</b> your cash out counts at the number our server sees when your tap arrives — usually a touch lower than what your screen shows. If the plane flies away before your tap reaches us, the bet is lost.</li>
              <li><b>Want an exact exit?</b> Set AUTO CASH OUT to a number like X2.00 — the server cashes you out the instant the plane reaches it, no tap needed. Best on a slow connection.</li>
              <li><b>Bet after takeoff?</b> It queues for the next round. Tap CANCEL to pull it back before that round takes off.</li>
            </ol>
            <footer>Every number shown is your total return. ₹ amounts are rounded down.</footer>
            <button type="button" {...press(() => setRulesOpen(false))}>GOT IT</button>
          </div>
        </>
      )}
    </div>
    </div>
  );
};

export default AvPage;
